import { logger } from "../utils/logger";
// ============================================
// API CLIENT FOR RUNCLOUD BACKEND
// VERSION: 8.0.0 - UNIFIED & UNIVERSAL
// ============================================

import { API_BASE_URL } from './apiConfig';
import { toast } from 'sonner@2.0.3';

const API_URL = API_BASE_URL;

// ============================================
// AUTH TOKEN MANAGEMENT
// ============================================

const getAuthToken = () => localStorage.getItem('auth_token') || '';

export const setAuthToken = (token: string) => {
  localStorage.setItem('auth_token', token);
};

export const clearAuthToken = () => {
  localStorage.removeItem('auth_token');
};

// ============================================
// ✅ UNIVERSAL API FUNCTION - USE THIS EVERYWHERE!
// ============================================

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  headers?: Record<string, string>;
  requiresAuth?: boolean;
  silent?: boolean;
  timeout?: number;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
}

/**
 * ✅ UNIVERSAL API FUNCTION
 * Use this for ALL API calls - public or authenticated
 * 
 * @example
 * // Public endpoint
 * const result = await api('/video-examples');
 * 
 * @example
 * // Admin endpoint (auth automatic)
 * const result = await api('/admin/videos', { method: 'POST', body: data });
 * 
 * @example
 * // Force auth for non-admin route
 * const result = await api('/my-profile', { requiresAuth: true });
 */
export async function api<T = any>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<ApiResponse<T>> {
  const {
    method = 'GET',
    body,
    headers = {},
    requiresAuth,
    silent = false,
    timeout = 20000,
  } = options;

  // Build URL
  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${API_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  // Auto-detect if auth is needed
  const isAdminRoute = endpoint.includes('/admin/');
  const needsAuth = requiresAuth || isAdminRoute;

  // Build headers
  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (needsAuth) {
    const token = getAuthToken();
    if (token) {
      finalHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  // Timeout controller
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // 🔧 FIX: Prepare body - avoid double-stringifying
  let finalBody: string | undefined = undefined;
  if (body) {
    if (typeof body === 'string') {
      finalBody = body;
    } else {
      finalBody = JSON.stringify(body);
    }
  }

  try {
    const response = await fetch(url, {
      method,
      mode: 'cors',
      credentials: 'omit',
      headers: finalHeaders,
      body: finalBody,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Parse response
    const responseText = await response.text();
    let data;
    
    try {
      data = responseText ? JSON.parse(responseText) : null;
    } catch {
      data = { rawResponse: responseText };
    }

    // Handle 401 Unauthorized
    if (response.status === 401) {
      clearAuthToken();
      const currentPath = window.location.hash || window.location.pathname;
      if (!currentPath.includes('/admin/login') && !currentPath.includes('/admin/signup')) {
        if (!silent) {
          toast.error('Session expired. Please log in again.', { duration: 4000 });
        }
        setTimeout(() => { window.location.hash = '#/admin/login'; }, 500);
      }
      return {
        success: false,
        error: 'Unauthorized',
        status: 401,
      };
    }

    // Success
    if (response.ok) {
      return {
        success: true,
        data: data,
        status: response.status,
      };
    }

    // Error response
    return {
      success: false,
      error: data?.error || data?.message || `HTTP ${response.status}`,
      status: response.status,
      data: data,
    };

  } catch (error: any) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      if (!silent) logger.error(`[API] Timeout: ${url}`);
      return {
        success: false,
        error: 'Request timeout',
        status: 504,
      };
    }

    if (error.message?.includes('Failed to fetch')) {
      if (!silent) logger.error(`[API] Network error: ${url}`);
      return {
        success: false,
        error: 'Network error',
        status: 503,
      };
    }

    if (!silent) logger.error(`[API] Error: ${url}`, error);
    return {
      success: false,
      error: error.message || 'Unknown error',
      status: 500,
    };
  }
}

// ============================================
// LEGACY COMPATIBILITY (for gradual migration)
// ============================================

export const apiFetch = async (endpoint: string, options: any = {}) => {
  logger.warn('⚠️ apiFetch is deprecated. Use api() instead.');
  const result = await api(endpoint, options);
  
  // Return a Response-like object for backward compatibility
  return new Response(JSON.stringify(result.data || { error: result.error }), {
    status: result.status || (result.success ? 200 : 500),
    headers: { 'Content-Type': 'application/json' },
  });
};

export const safeApiFetch = async (endpoint: string, options: any = {}) => {
  logger.warn('⚠️ safeApiFetch is deprecated. Use api() instead.');
  return await api(endpoint, options);
};

export const safeJsonParse = async (response: Response): Promise<any> => {
  logger.warn('⚠️ safeJsonParse is deprecated. Use api() instead.');
  const responseText = await response.text();
  if (!responseText || responseText.trim() === '') {
    return { success: false, error: 'Empty response from server' };
  }
  try {
    return JSON.parse(responseText);
  } catch (error) {
    return { success: false, error: 'Invalid JSON response', rawResponse: responseText };
  }
};

// ============================================
// AUTHENTICATION API
// ============================================

export const authAPI = {
  async login(email: string, password: string) {
    const result = await api('/admin/login', {
      method: 'POST',
      body: { email, password },
    });
    
    if (result.success && result.data) {
      const token = result.data.token || result.data.data?.token;
      if (token) {
        setAuthToken(token);
        return {
          success: true,
          session: { access_token: token },
          user: result.data.user || result.data.data?.user,
        };
      }
    }
    
    return result;
  },

  async logout() {
    clearAuthToken();
    return { success: true };
  },

  async getCurrentUser() {
    return await api('/auth/me', { requiresAuth: true });
  },

  async changePassword(currentPassword: string, newPassword: string) {
    return await api('/auth/change-password', {
      method: 'POST',
      requiresAuth: true,
      body: { currentPassword, newPassword },
    });
  },
};

// ============================================
// HOMEPAGE API
// ============================================

export const homepageAPI = {
  async getSectionOrder() {
    const response = await apiFetch('/homepage/section-order');
    return await response.json();
  },

  async setSectionOrder(order: string[]) {
    const response = await apiFetch('/admin/homepage/section-order', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(order),
    });
    return await response.json();
  },

  async getHero() {
    const response = await apiFetch('/homepage/hero');
    return await response.json();
  },

  async setHero(data: any) {
    const response = await apiFetch('/admin/homepage/hero', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getLogos() {
    const response = await apiFetch('/homepage/logos');
    return await response.json();
  },

  async setLogos(data: any) {
    const response = await apiFetch('/admin/homepage/logos', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getStrategicValue() {
    const response = await apiFetch('/homepage/strategic-value');
    return await response.json();
  },

  async setStrategicValue(data: any) {
    const response = await apiFetch('/admin/homepage/strategic-value', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getWhyChoose() {
    const response = await apiFetch('/homepage/why-choose');
    return await response.json();
  },

  async setWhyChoose(data: any) {
    const response = await apiFetch('/admin/homepage/why-choose', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getBundlePricing() {
    const response = await apiFetch('/homepage/bundle-pricing');
    return await response.json();
  },

  async setBundlePricing(data: any) {
    const response = await apiFetch('/admin/homepage/bundle-pricing', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getServices() {
    const response = await apiFetch('/homepage/services');
    return await response.json();
  },

  async setServices(data: any) {
    const response = await apiFetch('/admin/homepage/services', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getRealWorldImpact() {
    const response = await apiFetch('/homepage/real-world-impact');
    return await response.json();
  },

  async setRealWorldImpact(data: any) {
    const response = await apiFetch('/admin/homepage/real-world-impact', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getROICalculator() {
    const response = await apiFetch('/homepage/roi-calculator');
    return await response.json();
  },

  async setROICalculator(data: any) {
    const response = await apiFetch('/admin/homepage/roi-calculator', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

async getHowWeWork() {
  const response = await apiFetch('/homepage/how-we-work');
  return await response.json();
},

async setHowWeWork(data: any) {
  const response = await apiFetch('/admin/homepage/how-we-work', {
    method: 'PUT',
    requiresAuth: true,
    body: JSON.stringify(data),
  });
  return await response.json();
},

  async getHomepageFaq() {
    const response = await apiFetch('/homepage/faq');
    return await response.json();
  },

  async updateHomepageFaq(data: any) {
    const response = await apiFetch('/admin/homepage/faq', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getIndustryExpertise() {
    const response = await apiFetch('/homepage/industry-expertise');
    return await response.json();
  },

  async setIndustryExpertise(data: any) {
    const response = await apiFetch('/admin/homepage/industry-expertise', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getBlogPreview() {
    const response = await apiFetch('/homepage/blog-preview');
    return await response.json();
  },

  async setBlogPreview(data: any) {
    const response = await apiFetch('/admin/homepage/blog-preview', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getVideoPlanner() {
    const response = await apiFetch('/homepage/video-planner');
    return await response.json();
  },

  async setVideoPlanner(data: any) {
    const response = await apiFetch('/admin/homepage/video-planner', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getFAQ() {
    const response = await apiFetch('/homepage/faq');
    return await response.json();
  },

  async setFAQ(data: any) {
    const response = await apiFetch('/admin/homepage/faq', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async getAll() {
    const response = await apiFetch('/homepage/all');
    return await response.json();
  },
};

// ============================================
// GLOBAL CONTENT API
// ============================================

export const contentAPI = {
  // Trusted Clients (Global section used across all service pages)
  async getTrustedClients() {
    const response = await apiFetch('/content/trusted-clients');
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },

  async updateTrustedClients(data: any) {
    const response = await apiFetch('/admin/content/trusted-clients', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },

  // Why Choose Us (Global section)
  async getWhyChooseUs() {
    const response = await apiFetch('/content/why-choose-us');
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },

  async updateWhyChooseUs(data: any) {
    const response = await apiFetch('/admin/content/why-choose-us', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },

  // Testimonials (Global section)
  async getTestimonials() {
    const response = await apiFetch('/content/testimonials');
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },

  async updateTestimonials(data: any) {
    const response = await apiFetch('/admin/content/testimonials', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },

  // FAQs (Global section)
  async getFaqs() {
    const response = await apiFetch('/content/faqs');
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },

  async updateFaqs(data: any) {
    const response = await apiFetch('/admin/content/faqs', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },

  // Bundle Pricing (Global section)
  async getBundlePricing() {
    const response = await apiFetch('/content/bundle-pricing');
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },

  async updateBundlePricing(data: any) {
    const response = await apiFetch('/admin/content/bundle-pricing', {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      logger.error('Failed to parse response as JSON:', text.substring(0, 200));
      throw new Error('Invalid JSON response from server');
    }
  },
};

// ============================================
// SERVICE PAGES API
// ============================================

export const servicePageAPI = {
  // Hero Section
  async getHero(serviceType: string) {
    const response = await apiFetch(`/services/${serviceType}/hero`);
    return await response.json();
  },

  async setHero(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/hero`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  // Strategic Value Section
  async getStrategicValue(serviceType: string) {
    const response = await apiFetch(`/services/${serviceType}/strategic-value`);
    return await response.json();
  },

  async setStrategicValue(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/strategic-value`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  // Benefits Section
  async getBenefits(serviceType: string) {
    const response = await apiFetch(`/services/${serviceType}/benefits`);
    return await response.json();
  },

  async setBenefits(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/benefits`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  // Video Types Section (migrated to video-types endpoint for scalability)
  async getVideoTypes(serviceType: string) {
    const response = await apiFetch(`/admin/services/${serviceType}/video-types`);
    return await safeJsonParse(response);
  },

  async setVideoTypes(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/video-types`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await safeJsonParse(response);
  },

  // Industries Section
  async getIndustries(serviceType: string) {
    const response = await apiFetch(`/admin/services/${serviceType}/industries`);
    return await safeJsonParse(response);
  },

  async setIndustries(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/industries`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await safeJsonParse(response);
  },

  // Video Planner CTA Section
  async getVideoPlannerCta(serviceType: string) {
    const response = await apiFetch(`/admin/services/${serviceType}/planner-cta`);
    return await safeJsonParse(response);
  },

  async setVideoPlannerCta(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/planner-cta`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await safeJsonParse(response);
  },

  // Trusted Clients Section
  async getTrustedClients(serviceType: string) {
    const response = await apiFetch(`/services/${serviceType}/trusted-clients`);
    return await response.json();
  },

  async setTrustedClients(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/trusted-clients`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  // Video Examples Section
  async getVideoExamples(serviceType: string) {
    const response = await apiFetch(`/services/${serviceType}/video-examples`);
    return await response.json();
  },

  async setVideoExamples(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/video-examples`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  // Testimonials Section
  async getTestimonials(serviceType: string) {
    const response = await apiFetch(`/services/${serviceType}/testimonials`);
    return await response.json();
  },

  async setTestimonials(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/testimonials`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  // FAQs Section
  async getFAQs(serviceType: string) {
    const response = await apiFetch(`/services/${serviceType}/faqs`);
    return await response.json();
  },

  async setFAQs(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/faqs`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  // Pricing Section
  async getPricing(serviceType: string) {
    const response = await apiFetch(`/services/${serviceType}/pricing`);
    return await response.json();
  },

  async setPricing(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/pricing`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  // CTA Section
  async getCTA(serviceType: string) {
    const response = await apiFetch(`/services/${serviceType}/cta`);
    return await response.json();
  },

  async setCTA(serviceType: string, data: any) {
    const response = await apiFetch(`/admin/services/${serviceType}/cta`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },
};

// ============================================
// BLOGS API
// ============================================

export const blogsAPI = {
  async list() {
    const response = await apiFetch('/blog/posts');
    return await response.json();
  },

  async get(slug: string) {
    const response = await apiFetch(`/blog/posts/${slug}`);
    return await response.json();
  },

  async create(data: any) {
    const response = await apiFetch('/admin/blogs', {
      method: 'POST',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async update(slug: string, data: any) {
    const response = await apiFetch(`/admin/blogs/${slug}`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async delete(slug: string) {
    const response = await apiFetch(`/admin/blogs/${slug}`, {
      method: 'DELETE',
      requiresAuth: true,
    });
    return await response.json();
  },
};

// ============================================
// VIDEOS API
// ============================================

export const videosAPI = {
  async list() {
    const response = await apiFetch('/videos');
    return await response.json();
  },

  async create(data: any) {
    const response = await apiFetch('/admin/videos', {
      method: 'POST',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async update(id: string, data: any) {
    const response = await apiFetch(`/admin/videos/${id}`, {
      method: 'PUT',
      requiresAuth: true,
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async delete(id: string) {
    const response = await apiFetch(`/admin/videos/${id}`, {
      method: 'DELETE',
      requiresAuth: true,
    });
    return await response.json();
  },
};

// ============================================
// LEADS API
// ============================================

export const leadsAPI = {
  async list() {
    const response = await apiFetch('/admin/leads', {
      requiresAuth: true,
    });
    return await response.json();
  },

  async create(data: any) {
    const response = await apiFetch('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return await response.json();
  },

  async delete(id: number) {
    const response = await apiFetch(`/admin/leads/${id}`, {
      method: 'DELETE',
      requiresAuth: true,
    });
    return await response.json();
  },
};

// ============================================
// SUBSCRIBERS API
// ============================================

export const subscribersAPI = {
  async list() {
    const response = await apiFetch('/admin/subscribers', {
      requiresAuth: true,
    });
    return await response.json();
  },

  async subscribe(email: string, name?: string) {
    const response = await apiFetch('/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email, name }),
    });
    return await response.json();
  },

  async delete(id: number) {
    const response = await apiFetch(`/admin/subscribers/${id}`, {
      method: 'DELETE',
      requiresAuth: true,
    });
    return await response.json();
  },
};

// ============================================
// ABOUT PAGE API
// ============================================

export const aboutAPI = {
  // Hero Section
  async getHero() {
    return await api('/about/hero', { silent: true });
  },

  async setHero(data: any) {
    return await api('/admin/about/hero', {
      method: 'PUT',
      body: data,
    });
  },

  // Story Overview
  async getStoryOverview() {
    return await api('/about/story-overview', { silent: true });
  },

  async setStoryOverview(data: any) {
    return await api('/admin/about/story-overview', {
      method: 'PUT',
      body: data,
    });
  },

  // Story Journey
  async getStoryJourney() {
    return await api('/about/story-journey', { silent: true });
  },

  async setStoryJourney(data: any) {
    return await api('/admin/about/story-journey', {
      method: 'PUT',
      body: data,
    });
  },

  // Story Mission & Vision
  async getStoryMissionVision() {
    return await api('/about/story-mission-vision', { silent: true });
  },

  async setStoryMissionVision(data: any) {
    return await api('/admin/about/story-mission-vision', {
      method: 'PUT',
      body: data,
    });
  },

  // Story Core Values
  async getStoryCoreValues() {
    return await api('/about/story-core-values', { silent: true });
  },

  async setStoryCoreValues(data: any) {
    return await api('/admin/about/story-core-values', {
      method: 'PUT',
      body: data,
    });
  },

  // People Overview
  async getPeopleOverview() {
    return await api('/about/people-overview', { silent: true });
  },

  async setPeopleOverview(data: any) {
    return await api('/admin/about/people-overview', {
      method: 'PUT',
      body: data,
    });
  },

  // People Expertise
  async getPeopleExpertise() {
    return await api('/about/people-expertise', { silent: true });
  },

  async setPeopleExpertise(data: any) {
    return await api('/admin/about/people-expertise', {
      method: 'PUT',
      body: data,
    });
  },

  // People Culture
  async getPeopleCulture() {
    return await api('/about/people-culture', { silent: true });
  },

  async setPeopleCulture(data: any) {
    return await api('/admin/about/people-culture', {
      method: 'PUT',
      body: data,
    });
  },

  // Work Process
  async getWorkProcess() {
    return await api('/about/work-process', { silent: true });
  },

  async setWorkProcess(data: any) {
    return await api('/admin/about/work-process', {
      method: 'PUT',
      body: data,
    });
  },

  // Work Benefits
  async getWorkBenefits() {
    return await api('/about/work-benefits', { silent: true });
  },

  async setWorkBenefits(data: any) {
    return await api('/admin/about/work-benefits', {
      method: 'PUT',
      body: data,
    });
  },

  // Section Order
  async getSectionOrder() {
    return await api('/about/section-order', { silent: true });
  },

  async setSectionOrder(data: any) {
    return await api('/admin/about/section-order', {
      method: 'PUT',
      body: data,
    });
  },

  // Section Visibility
  async setSectionVisibility(sectionId: string, visible: boolean) {
    return await api('/admin/about/section-visibility', {
      method: 'PUT',
      body: { sectionId, visible },
    });
  },

  // Settings
  async getSettings() {
    return await api('/about/settings', { silent: true });
  },

  async setSettings(data: any) {
    return await api('/admin/about/settings', {
      method: 'PUT',
      body: data,
    });
  },
};

// ============================================
// PROCESS PAGE API
// ============================================

export const processAPI = {
  // Hero Section
  async getHero() {
    return await api('/process-page/hero', { silent: true });
  },

  async setHero(data: any) {
    return await api('/admin/process-page/hero', {
      method: 'PUT',
      body: data,
    });
  },

  // Metrics Section
  async getMetrics() {
    return await api('/process-page/metrics', { silent: true });
  },

  async setMetrics(data: any) {
    return await api('/admin/process-page/metrics', {
      method: 'PUT',
      body: data,
    });
  },

  // Process Steps (All)
  async getSteps() {
    return await api('/process-page/steps', { silent: true });
  },

  async setSteps(data: any) {
    return await api('/admin/process-page/steps', {
      method: 'PUT',
      body: data,
    });
  },

  // Individual Step
  async getStep(stepNumber: string) {
    return await api(`/process-page/steps/${stepNumber}`, { silent: true });
  },

  async setStep(stepNumber: string, data: any) {
    return await api(`/admin/process-page/steps/${stepNumber}`, {
      method: 'PUT',
      body: data,
    });
  },

  // Why Six Steps Section
  async getWhySixSteps() {
    return await api('/process-page/why-six-steps', { silent: true });
  },

  async setWhySixSteps(data: any) {
    return await api('/admin/process-page/why-six-steps', {
      method: 'PUT',
      body: data,
    });
  },

  // Guarantee Section
  async getGuarantee() {
    return await api('/process-page/guarantee', { silent: true });
  },

  async setGuarantee(data: any) {
    return await api('/admin/process-page/guarantee', {
      method: 'PUT',
      body: data,
    });
  },

  // Final CTA Section
  async getFinalCTA() {
    return await api('/process-page/final-cta', { silent: true });
  },

  async setFinalCTA(data: any) {
    return await api('/admin/process-page/final-cta', {
      method: 'PUT',
      body: data,
    });
  },

  // FAQ Section
  async getFAQ() {
    return await api('/process-page/faq', { silent: true });
  },

  async setFAQ(data: any) {
    return await api('/admin/process-page/faq', {
      method: 'PUT',
      body: data,
    });
  },

  async addFAQItem(data: any) {
    return await api('/admin/process-page/faq/item', {
      method: 'POST',
      body: data,
    });
  },

  async updateFAQItem(id: string, data: any) {
    return await api(`/admin/process-page/faq/item/${id}`, {
      method: 'PUT',
      body: data,
    });
  },

  async deleteFAQItem(id: string) {
    return await api(`/admin/process-page/faq/item/${id}`, {
      method: 'DELETE',
    });
  },

  async reorderFAQ(newOrder: string[]) {
    return await api('/admin/process-page/faq/reorder', {
      method: 'PUT',
      body: { newOrder },
    });
  },

  // Settings (SEO, Slug, etc.)
  async getSettings() {
    return await api('/admin/process-page/settings', { silent: true });
  },

  async setSettings(data: any) {
    return await api('/admin/process-page/settings', {
      method: 'PUT',
      body: data,
    });
  },

  // Preview (Dashboard Overview)
  async getPreview() {
    return await api('/admin/process-page/preview', { silent: true });
  },

  // Section Visibility
  async setSectionVisibility(sectionId: string, visible: boolean) {
    return await api(`/admin/process-page/sections/${sectionId}/visibility`, {
      method: 'PUT',
      body: { visible },
    });
  },

  // Section Order
  async getSectionOrder() {
    return await api('/admin/process-page/section-order', { silent: true });
  },

  async setSectionOrder(sections: any[]) {
    return await api('/admin/process-page/section-order', {
      method: 'PUT',
      body: { sections },
    });
  },
};

// ============================================
// INDUSTRY PAGES API
// ============================================

export const industryAPI = {
  // List all industries
  async list() {
    return await api('/industries');
  },

  // Get single industry by slug
  async get(slug: string) {
    return await api(`/industries/${slug}`);
  },

  // Get industry hero section
  async getHero(slug: string) {
    return await api(`/industries/${slug}/hero`);
  },

  async setHero(slug: string, data: any) {
    return await api(`/admin/industries/${slug}/hero`, {
      method: 'PUT',
      body: data,
    });
  },

  // Get industry challenges section
  async getChallenges(slug: string) {
    return await api(`/industries/${slug}/challenges`);
  },

  async setChallenges(slug: string, data: any) {
    return await api(`/admin/industries/${slug}/challenges`, {
      method: 'PUT',
      body: data,
    });
  },

  // Get industry solutions section
  async getSolutions(slug: string) {
    return await api(`/industries/${slug}/solutions`);
  },

  async setSolutions(slug: string, data: any) {
    return await api(`/admin/industries/${slug}/solutions`, {
      method: 'PUT',
      body: data,
    });
  },

  // Get industry case studies
  async getCaseStudies(slug: string) {
    return await api(`/industries/${slug}/case-studies`);
  },

  async setCaseStudies(slug: string, data: any) {
    return await api(`/admin/industries/${slug}/case-studies`, {
      method: 'PUT',
      body: data,
    });
  },

  // Get industry video examples
  async getVideoExamples(slug: string) {
    return await api(`/industries/${slug}/video-examples`);
  },

  async setVideoExamples(slug: string, data: any) {
    return await api(`/admin/industries/${slug}/video-examples`, {
      method: 'PUT',
      body: data,
    });
  },

  // Get industry testimonials
  async getTestimonials(slug: string) {
    return await api(`/industries/${slug}/testimonials`);
  },

  async setTestimonials(slug: string, data: any) {
    return await api(`/admin/industries/${slug}/testimonials`, {
      method: 'PUT',
      body: data,
    });
  },

  // Get industry FAQs
  async getFAQs(slug: string) {
    return await api(`/industries/${slug}/faqs`);
  },

  async setFAQs(slug: string, data: any) {
    return await api(`/admin/industries/${slug}/faqs`, {
      method: 'PUT',
      body: data,
    });
  },

  // Get industry CTA
  async getCTA(slug: string) {
    return await api(`/industries/${slug}/cta`);
  },

  async setCTA(slug: string, data: any) {
    return await api(`/admin/industries/${slug}/cta`, {
      method: 'PUT',
      body: data,
    });
  },

  // Create new industry
  async create(data: any) {
    return await api('/admin/industries', {
      method: 'POST',
      body: data,
    });
  },

  // Update industry
  async update(slug: string, data: any) {
    return await api(`/admin/industries/${slug}`, {
      method: 'PUT',
      body: data,
    });
  },

  // Delete industry
  async delete(slug: string) {
    return await api(`/admin/industries/${slug}`, {
      method: 'DELETE',
    });
  },

  // Get industry settings
  async getSettings(slug: string) {
    return await api(`/industries/${slug}/settings`);
  },

  async setSettings(slug: string, data: any) {
    return await api(`/admin/industries/${slug}/settings`, {
      method: 'PUT',
      body: data,
    });
  },

  // Get all industry data at once (for editor)
  async getAll(slug: string) {
    return await api(`/admin/industries/${slug}/all`);
  },
};

// ============================================
// ROI CALCULATOR PAGE API
// ============================================

export const roiPageAPI = {
  // Hero Section
  async getHero() {
    return await api('/roi-page/hero', { silent: true });
  },

  async setHero(data: any) {
    return await api('/roi-page/hero', {
      method: 'PUT',
      body: data,
    });
  },

  // CTA Section
  async getCTA() {
    return await api('/roi-page/cta', { silent: true });
  },

  async setCTA(data: any) {
    return await api('/roi-page/cta', {
      method: 'PUT',
      body: data,
    });
  },

  // Benchmarks Section
  async getBenchmarks() {
    return await api('/roi-page/benchmarks', { silent: true });
  },

  async setBenchmarks(data: any) {
    return await api('/roi-page/benchmarks', {
      method: 'PUT',
      body: data,
    });
  },

  // Settings
  async getSettings() {
    return await api('/roi-page/settings', { silent: true });
  },

  async setSettings(data: any) {
    return await api('/roi-page/settings', {
      method: 'PUT',
      body: data,
    });
  },

  // Section Order
  async getSectionOrder() {
    return await api('/roi-page/section-order', { silent: true });
  },

  async setSectionOrder(sections: any[]) {
    return await api('/roi-page/section-order', {
      method: 'PUT',
      body: { sections },
    });
  },

  // PDF Generation
  async generatePDF(data: any) {
    return await api('/roi-calculator/generate-pdf', {
      method: 'POST',
      body: data,
    });
  },

  // PDF Template Admin (CMS)
  async getPDFTemplate() {
    return await api('/admin/roi-pdf-template', { silent: true });
  },

  async setPDFTemplate(data: any) {
    return await api('/admin/roi-pdf-template', {
      method: 'PUT',
      body: data,
    });
  },

  async previewPDF(templateSettings: any) {
    return await api('/admin/roi-pdf-template/preview', {
      method: 'POST',
      body: templateSettings,
    });
  },

  // ROI Reports Admin
  async listReports() {
    return await api('/admin/roi-reports');
  },

  async getReport(reportId: string) {
    return await api(`/admin/roi-reports/${reportId}`);
  },

  async deleteReport(reportId: string) {
    return await api(`/admin/roi-reports/${reportId}`, {
      method: 'DELETE',
    });
  },

  async trackDownload(reportId: string) {
    return await api(`/admin/roi-reports/${reportId}/track-download`, {
      method: 'POST',
    });
  },

  // Get all ROI page data at once
  async getAll() {
    return await api('/roi-page/all', { silent: true });
  },
};

// ============================================
// MENU/HEADER CMS API
// ============================================

export const menuAPI = {
  // Get Published Menu (Public)
  async getPublished() {
    return await api('/menu/published', { silent: true });
  },

  // Get Draft Menu (Admin)
  async getDraft() {
    return await api('/admin/menu/draft');
  },

  // Get Menu by Status (Admin)
  async getByStatus(status: 'published' | 'draft') {
    return await api(`/admin/menu/${status}`);
  },

  // Save Draft Menu (Admin)
  async saveDraft(configData: any, userId: string = 'admin') {
    return await api('/admin/menu/draft', {
      method: 'POST',
      body: { configData, userId },
    });
  },

  // Update Draft Menu (Admin)
  async updateDraft(configData: any, userId: string = 'admin') {
    return await api('/admin/menu/draft', {
      method: 'PUT',
      body: { configData, userId },
    });
  },

  // Publish Menu (Admin)
  async publish(userId: string = 'admin') {
    return await api('/admin/menu/publish', {
      method: 'POST',
      body: { userId },
    });
  },

  // Discard Draft (Admin)
  async discardDraft() {
    return await api('/admin/menu/discard-draft', {
      method: 'POST',
      body: {},
    });
  },

  // Get Revision History (Admin)
  async getRevisions(limit: number = 10) {
    return await api(`/admin/menu/revisions?limit=${limit}`);
  },

  // Restore Revision (Admin)
  async restoreRevision(revisionTimestamp: string, userId: string = 'admin') {
    return await api('/admin/menu/restore-revision', {
      method: 'POST',
      body: { revisionTimestamp, userId },
    });
  },

  // Get Menu Summary (Admin)
  async getSummary() {
    return await api('/admin/menu/summary');
  },

  // ============================================
  // TOP-LEVEL MENU ITEMS CRUD
  // ============================================

  // Add Top-Level Item
  async addTopLevelItem(item: any, userId: string = 'admin') {
    return await api('/admin/menu/items', {
      method: 'POST',
      body: { item, userId },
    });
  },

  // Update Top-Level Item
  async updateTopLevelItem(itemId: string, updates: any, userId: string = 'admin') {
    return await api(`/admin/menu/items/${itemId}`, {
      method: 'PUT',
      body: { updates, userId },
    });
  },

  // Delete Top-Level Item
  async deleteTopLevelItem(itemId: string, userId: string = 'admin') {
    return await api(`/admin/menu/items/${itemId}?userId=${userId}`, {
      method: 'DELETE',
    });
  },

  // Reorder Top-Level Items
  async reorderTopLevelItems(orderedIds: string[], userId: string = 'admin') {
    return await api('/admin/menu/items/reorder', {
      method: 'POST',
      body: { orderedIds, userId },
    });
  },

  // ============================================
  // MEGA MENU ITEMS CRUD
  // ============================================

  // Add Mega Menu Item
  async addMegaMenuItem(navId: string, menuItem: any, userId: string = 'admin') {
    return await api(`/admin/menu/${navId}/items`, {
      method: 'POST',
      body: { menuItem, userId },
    });
  },

  // Update Mega Menu Item
  async updateMegaMenuItem(navId: string, menuItemId: string, updates: any, userId: string = 'admin') {
    return await api(`/admin/menu/${navId}/items/${menuItemId}`, {
      method: 'PUT',
      body: { updates, userId },
    });
  },

  // Delete Mega Menu Item
  async deleteMegaMenuItem(navId: string, menuItemId: string, userId: string = 'admin') {
    return await api(`/admin/menu/${navId}/items/${menuItemId}?userId=${userId}`, {
      method: 'DELETE',
    });
  },

  // Reorder Mega Menu Items
  async reorderMegaMenuItems(navId: string, orderedIds: string[], userId: string = 'admin') {
    return await api(`/admin/menu/${navId}/items/reorder`, {
      method: 'POST',
      body: { orderedIds, userId },
    });
  },
};

export { API_URL };